import { useState } from "react";
import type { CommunityReply } from "@/mocks/posts";
import { formatCount } from "@/utils/format";

function ReplyBody({ reply }: { reply: CommunityReply }) {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(reply.likes);

  function toggleLike() {
    setLiked((prev) => {
      setLikes((count) => (prev ? count - 1 : count + 1));
      return !prev;
    });
  }

  return (
    <>
      <div className="flex items-start gap-3.5">
        <span
          className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-medium ${
            reply.essence
              ? "bg-accent-100 text-accent-800"
              : "bg-secondary-100 text-secondary-700"
          }`}
        >
          {reply.author.slice(0, 1)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-sm font-medium text-foreground-900">{reply.author}</span>
            {reply.essence && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-accent-100 text-accent-800 text-[11px]">
                <i className="ri-award-line"></i>
                加精神回复
              </span>
            )}
            <span className="px-2 py-0.5 rounded-md bg-background-200 text-foreground-600 text-[11px]">
              {reply.role}
            </span>
            <span className="text-xs text-foreground-400">{reply.time}</span>
          </div>

          <p
            className={`mt-3 leading-relaxed text-foreground-800 ${
              reply.essence ? "font-heading text-base md:text-lg" : "text-sm md:text-base"
            }`}
          >
            {reply.content}
          </p>

          <div className="mt-3 flex items-center gap-4">
            <button
              type="button"
              onClick={toggleLike}
              className={`inline-flex items-center gap-1.5 text-xs transition-colors cursor-pointer ${
                liked ? "text-accent-600" : "text-foreground-500 hover:text-accent-600"
              }`}
            >
              <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
              {formatCount(likes)}
            </button>
            <span className="inline-flex items-center gap-1.5 text-xs text-foreground-400">
              <i className="ri-chat-off-line"></i>
              围观中
            </span>
          </div>
        </div>
      </div>

      {reply.replies && reply.replies.length > 0 && (
        <div className="mt-4 ml-5 md:ml-14 pl-4 border-l-2 border-background-200 space-y-4">
          {reply.replies.map((child, index) => (
            <div key={`${child.author}-${index}`} className="flex items-start gap-3">
              <span className="w-7 h-7 shrink-0 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[11px] font-medium">
                {child.author.slice(0, 1)}
              </span>
              <div className="min-w-0">
                <p className="text-xs text-foreground-600">
                  <span className="font-medium text-foreground-800">{child.author}</span>
                  <span className="ml-2 text-foreground-400">{formatCount(child.likes)} 赞</span>
                </p>
                <p className="mt-1 text-sm text-foreground-700 leading-relaxed">{child.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function ReplyThread({ reply }: { reply: CommunityReply }) {
  return (
    <article className="py-6 border-b border-background-200 last:border-0">
      <ReplyBody reply={reply} />
    </article>
  );
}